import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import DataTable from '../../../sharedModules/dataTable';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { getCollectionsSelectedLocation } from '../../navigation/components/CollectionsLocationToggle';
import { formatDollar } from '../../clients/utils/format';
import { Statement } from '../model';
import { statementsQueryKey, useStatements } from '../hooks/useStatements';
import { useUpdateStatementEnabled } from '../hooks/useUpdateStatementEnabled';
import StatementPdfsModal from '../components/StatementPdfsModal';

type TriState = 'all' | 'yes' | 'no';

const ROW_CLICK_TAG = '___react-data-table-allow-propagation___';

interface ModalState {
  open: boolean;
  acctNo: string;
  company: string;
}

const filterRows = (rows: Statement[], company: string, billCode: string, enabled: TriState): Statement[] => {
  const qCompany = company.trim().toLowerCase();
  const qBillCode = billCode.trim().toLowerCase();
  return rows.filter((row) => {
    if (qCompany && !(row.company ?? '').toLowerCase().includes(qCompany)) return false;
    if (qBillCode && !(row.bill_code ?? '').toLowerCase().includes(qBillCode)) return false;
    if (enabled !== 'all' && row.statement_enabled !== (enabled === 'yes')) return false;
    return true;
  });
};

const buildColumns = (
  onPdfsClick: (row: Statement) => void,
  onToggle: (row: Statement, checked: boolean) => void,
  isToggling: boolean,
) => [
  {
    name: 'Company',
    selector: (row: Statement) => row.company,
    sortable: true,
    grow: 2,
    cell: (row: Statement) => {
      const toggleDisabled = isToggling || !row.collections_client_id;

      const handleCompanyNameClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (toggleDisabled) return;
        onToggle(row, !row.statement_enabled);
      };

      const switchControl = (
        <Switch
          checked={row.statement_enabled}
          onChange={(e) => onToggle(row, e.target.checked)}
          color="primary"
          size="small"
          disabled={toggleDisabled}
          inputProps={{
            'aria-label': `Statement enabled for ${row.company}`,
            'aria-disabled': toggleDisabled,
          }}
        />
      );

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          {switchControl}
          <Box
            onClick={handleCompanyNameClick}
            sx={{
              flex: 1,
              py: 0.5,
              cursor: toggleDisabled ? 'default' : 'pointer',
            }}
          >
            <Typography variant="body2">{row.company}</Typography>
          </Box>
        </Box>
      );
    },
  },
  { name: 'Bill Code', selector: (row: Statement) => row.bill_code, sortable: true, width: '120px' },
  {
    name: 'Total Invoiced',
    selector: (row: Statement) => row.total_invoiced,
    cell: (row: Statement) => (
      <Typography variant="body2" data-tag={ROW_CLICK_TAG}>
        {formatDollar(row.total_invoiced)}
      </Typography>
    ),
    sortable: true,
    width: '140px',
  },
  {
    name: 'Total Balance',
    selector: (row: Statement) => row.total_balance,
    cell: (row: Statement) => (
      <Typography
        variant="body2"
        data-tag={ROW_CLICK_TAG}
        sx={{ color: row.total_balance > 0 ? 'error.main' : 'text.primary' }}
      >
        {formatDollar(row.total_balance)}
      </Typography>
    ),
    sortable: true,
    width: '140px',
  },
  {
    name: 'Invoices',
    selector: (row: Statement) => row.invoice_count,
    sortable: true,
    center: true,
    width: '110px',
  },
  {
    name: 'PDFs',
    selector: (row: Statement) => row.matched_pdf_count,
    sortable: true,
    center: true,
    width: '120px',
    cell: (row: Statement) => {
      if (row.invoice_count === 0) {
        return (
          <Typography variant="body2" data-tag={ROW_CLICK_TAG}>
            0
          </Typography>
        );
      }
      if (!row.collections_client_id) {
        return (
          <Typography variant="body2" data-tag={ROW_CLICK_TAG}>
            {`${row.matched_pdf_count} / ${row.invoice_count}`}
          </Typography>
        );
      }
      const allMatched = row.matched_pdf_count === row.invoice_count;
      return (
        <Chip
          icon={<PictureAsPdfIcon />}
          label={`${row.matched_pdf_count} / ${row.invoice_count}`}
          size="small"
          color={allMatched ? 'primary' : 'error'}
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            onPdfsClick(row);
          }}
          sx={{ cursor: 'pointer', px: 1, py: 0.5, height: 'auto' }}
          aria-label={`${row.matched_pdf_count} of ${row.invoice_count} invoices have PDFs for ${row.company}`}
        />
      );
    },
  },
];

const StatementListPage: React.FC = () => {
  const [location] = useState(() => getCollectionsSelectedLocation());
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useStatements(location, true);
  const { updateStatementEnabled, isLoading: isToggling } = useUpdateStatementEnabled();
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);

  const [statements, setStatements] = useState<Statement[]>([]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [billCodeFilter, setBillCodeFilter] = useState('');
  const [enabledFilter, setEnabledFilter] = useState<TriState>('all');
  const [modal, setModal] = useState<ModalState>({ open: false, acctNo: '', company: '' });

  useEffect(() => {
    if (data?.data) setStatements(data.data);
  }, [data]);

  const filtered = useMemo(
    () => filterRows(statements, companyFilter, billCodeFilter, enabledFilter),
    [statements, companyFilter, billCodeFilter, enabledFilter],
  );

  const handleClearFilters = () => {
    setCompanyFilter('');
    setBillCodeFilter('');
    setEnabledFilter('all');
  };

  const handleToggle = async (row: Statement, enabled: boolean) => {
    if (!row.collections_client_id) {
      showErrorSnackBar?.('Statement not available for update.');
      return;
    }

    const previous = row.statement_enabled;
    setStatements((prev) =>
      prev.map((s) => (s.acct_no === row.acct_no ? { ...s, statement_enabled: enabled } : s)),
    );
    try {
      await updateStatementEnabled({ acctNo: row.acct_no, location, statementEnabled: enabled });
      showSuccessSnackBar?.(`Statement ${enabled ? 'enabled' : 'disabled'} for ${row.company}.`);
    } catch (error: any) {
      setStatements((prev) =>
        prev.map((s) => (s.acct_no === row.acct_no ? { ...s, statement_enabled: previous } : s)),
      );
      const serverMessage = error?.response?.data?.errors?.[0] || error?.response?.data?.error;
      showErrorSnackBar?.(serverMessage || 'Failed to update statement status.');
    }
  };

  const handlePdfsClick = (row: Statement) => {
    setModal({ open: true, acctNo: row.acct_no, company: row.company });
  };

  const handlePdfUploaded = () => {
    void queryClient.invalidateQueries({ queryKey: statementsQueryKey(location) });
  };

  const tableData = isError ? [] : filtered;

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Statements - Clients" titleIcon={<DescriptionIcon />} />
      </Grid>
      {isError && (
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Alert severity="error">Failed to load statements.</Alert>
        </Grid>
      )}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={3}>
            <TextField
              label="Company Name"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              size="small"
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Bill Code"
              value={billCodeFilter}
              onChange={(e) => setBillCodeFilter(e.target.value)}
              size="small"
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="statements-filter-enabled">Active</InputLabel>
              <Select
                labelId="statements-filter-enabled"
                label="Active"
                value={enabledFilter}
                onChange={(e) => setEnabledFilter(e.target.value as TriState)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md="auto">
            <Button variant="outlined" size="small" onClick={handleClearFilters} sx={{ mb: 0.25 }}>
              Clear filters
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} sx={{ mt: 2.5 }}>
        <Card>
          <DataTable
            columns={buildColumns(handlePdfsClick, handleToggle, isToggling)}
            data={tableData}
            progressPending={isLoading}
            pagination
            paginationPerPage={25}
            paginationRowsPerPageOptions={[25, 50, 100]}
            defaultSortField="total_balance"
            defaultSortAsc={false}
            highlightOnHover
            striped
            noHeader
          />
        </Card>
      </Grid>
      <StatementPdfsModal
        open={modal.open}
        onClose={() => setModal({ open: false, acctNo: '', company: '' })}
        acctNo={modal.acctNo}
        company={modal.company}
        location={location}
        onPdfUploaded={handlePdfUploaded}
      />
    </Grid>
  );
};

export default StatementListPage;
